'use client'

import clsx from 'clsx'
import { useState, useEffect, useMemo } from 'react'
import { type Swiper as SwiperType } from 'swiper'

interface SwiperNavigationProps {
	swiperRef: React.MutableRefObject<SwiperType | null>
	showArrows?: boolean
	showCounter?: boolean
	showProgressbar?: boolean
	title?: { show: boolean; text: string }
	navigationClass?: string
}

const ASwiperNavigation = ({
	swiperRef,
	showArrows = true,
	showCounter = true,
	showProgressbar = true,
	title = { show: false, text: '' },
	navigationClass
}: SwiperNavigationProps) => {
	const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)
	const [activeIndex, setActiveIndex] = useState(0)
	const [totalSlidesViews, setTotalSlidesViews] = useState(0)

	// Calculate if current slide is first or last based on activeIndex and totalSlidesViews
	const isCurrentSlideFirst = useMemo(() => activeIndex === 0, [activeIndex])
	const isCurrentSlideLast = useMemo(
		() => totalSlidesViews > 0 && activeIndex >= totalSlidesViews - 1,
		[activeIndex, totalSlidesViews]
	)

	// Poll for swiper instance initialization
	useEffect(() => {
		const checkInterval = setInterval(() => {
			if (swiperRef.current && !swiperRef.current.destroyed) {
				setSwiperInstance(swiperRef.current)
				clearInterval(checkInterval)
			}
		}, 50)

		return () => clearInterval(checkInterval)
	}, [swiperRef])

	useEffect(() => {
		if (!swiperInstance || swiperInstance.destroyed) return

		const updateNavigationState = () => {
			// Safety check in case swiper gets destroyed
			if (!swiperInstance || swiperInstance.destroyed) return

			// Use realIndex for better loop support, fallback to activeIndex
			const index = swiperInstance.params?.loop
				? swiperInstance.realIndex
				: swiperInstance.activeIndex
			setActiveIndex(index)
		}

		// Listen to multiple events that can change navigation state
		swiperInstance.on('slideChange', updateNavigationState)
		swiperInstance.on('slideChangeTransitionEnd', updateNavigationState)
		swiperInstance.on('resize', updateNavigationState)
		swiperInstance.on('update', updateNavigationState)

		// Initialize with current values
		updateNavigationState()

		return () => {
			if (!swiperInstance.destroyed) {
				swiperInstance.off('slideChange', updateNavigationState)
				swiperInstance.off('slideChangeTransitionEnd', updateNavigationState)
				swiperInstance.off('resize', updateNavigationState)
				swiperInstance.off('update', updateNavigationState)
			}
		}
	}, [swiperInstance])

	useEffect(() => {
		if (!swiperInstance || swiperInstance.destroyed) return

		const calculateTotalSlides = () => {
			if (!swiperInstance || swiperInstance.destroyed) return

			const params = swiperInstance.params

			// For loop mode, use the actual number of slides without duplicates
			if (params?.loop) {
				const realSlides =
					swiperInstance.slides?.filter(
						slide => !slide.classList.contains('swiper-slide-duplicate')
					).length || 0
				setTotalSlidesViews(realSlides)
				return
			}

			// Pre slidesPerView: 'auto' použi snapGrid alebo slides.length
			if (params?.slidesPerView === 'auto') {
				const snapGridLength = swiperInstance.snapGrid?.length || 0
				const slidesLength = swiperInstance.slides?.length || 0
				setTotalSlidesViews(snapGridLength || slidesLength)
			} else {
				// Pre ostatné prípady použi klasický výpočet
				const totalSlides = Math.ceil(
					(swiperInstance.slides?.length || 0) /
						(swiperInstance.slidesPerViewDynamic?.() || 1)
				)
				setTotalSlidesViews(totalSlides)
			}
		}

		// Calculate initial total slides
		calculateTotalSlides()

		// Only add event listener on client side
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', calculateTotalSlides)

			return () => {
				window.removeEventListener('resize', calculateTotalSlides)
			}
		}
	}, [swiperInstance])

	const paginationClass = navigationClass ? `${navigationClass}-pagination` : 'swiper-pagination'

	// Only render navigation when swiper instance is ready and not destroyed
	if (!swiperInstance || swiperInstance.destroyed) {
		return null
	}

	if (!showProgressbar && !showCounter && !showArrows) {
		return null
	}

	return (
		<div className='mx-auto max-w-screen-xl py-5 flex flex-col items-center justify-center gap-3 lg:pt-10 xl:mb-[20px]'>
			{showProgressbar && totalSlidesViews > 1 && (
				<div
					className={`${paginationClass} swiper-pagination-progressbar w-full h-[4px] bg-gray-200 rounded-full overflow-hidden relative`}
				>
					{totalSlidesViews > 0 && (
						<div
							className={clsx(
								'absolute top-0 h-full bg-medante-purple rounded-full transition-all duration-300'
							)}
							style={{
								width: `${100 / totalSlidesViews}%`,
								left: `${(activeIndex * 100) / totalSlidesViews}%`
							}}
						/>
					)}
				</div>
			)}
			<div className='flex items-center justify-between w-full px-4 sm:px-6 xl:px-8 pt-4'>
				{title.show && <h2 className='text-lg uppercase'>{title.text}</h2>}

				{showCounter && totalSlidesViews > 1 && (
					<p>
						{activeIndex + 1} / {totalSlidesViews}
					</p>
				)}
				{showArrows && (
					<>
						<div className='swiper_controls_side'>
							<button
								onClick={() => swiperRef.current?.slidePrev()}
								title='Predchádzajúci'
								name='predchadzajuci'
								className={clsx(
									'btn_circle toleft touch-manipulation',
									navigationClass
										? `${navigationClass}-prev`
										: 'custom-swiper-prev',
									isCurrentSlideFirst && 'swiper-button-disabled'
								)}
							>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_l' />
									</svg>
								</span>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_l' />
									</svg>
								</span>
							</button>
							<button
								onClick={() => swiperRef.current?.slideNext()}
								title='Následujúci'
								name='nasledujuci'
								className={clsx(
									'btn_circle  touch-manipulation',
									navigationClass
										? `${navigationClass}-next`
										: 'custom-swiper-next',
									isCurrentSlideLast && 'swiper-button-disabled'
								)}
							>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_r' />
									</svg>
								</span>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_r' />
									</svg>
								</span>
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default ASwiperNavigation
